package com.neurobs.ccabserver.administrator.controller

import com.neurobs.ccab.TestUpdateData
import com.neurobs.ccab.db.TestData
import com.neurobs.js.ccab.CCABServer
import com.neurobs.js.controller.{AdminListAdapter, AdminListController, AdminPanelDelegate}
import com.neurobs.js.util.StringUtilities.dateString
import com.neurobs.js.dom.ElementFactory._
import com.neurobs.js.dom.DOMViewElement._
import com.neurobs.js.dom.RecordTableView.CommandLinkCell
import com.neurobs.js.dom.{InputTextBox, TextAreaTextBox}
import com.neurobs.js.view.{Form, Gui}
import scalatags.JsDom.all._

import scala.concurrent.{Future, Promise}

class AdminTestListController(gui: Gui) extends AdminListAdapter[TestData] {

	override def title = "Tests"

	override def itemName = "Test"

	override def columnHeaders = Seq(
		"Created",
		"Identifier",
		"Name",
		"Description",
		"",
		""
	)

	override def itemRow(t: TestData, delegate: Option[AdminPanelDelegate], controller: AdminListController[TestData]) = Seq(
		dateString(t.createdAt),
		t.identifier,
		t.displayName,
		t.description,
		CommandLinkCell("edit", () => createOrEdit(Some(t), controller)),
		CommandLinkCell("delete", () => delete(t, controller))
	)

	override val create = Some(createOrEdit(None, _))

	override def get(): Future[Either[String, Seq[TestData]]] = {
		val promise = Promise[Either[String, Seq[TestData]]]
		CCABServer.getTests {
			case Left(error) => promise.success(Left(error.message))
			case Right(data) => promise.success(Right(data))
		}
		promise.future
	}

	private def delete(t: TestData, c: AdminListController[TestData]): Unit = {
		val form = makeForm(
			width = 30,
			title = s"Delete Test ${t.displayName}",
			description = "",
			elements = Array(),
			useCancel = true,
			submitCaption = "Delete"
		)

		def process(callback: (Option[String]) => Unit): Either[String,Form.Process] = Right(
			CCABServer.deleteTest(t.id) {
				case Left(err) => callback(Some(err.message))
				case Right(res) => res.error match {
					case Some(err) => callback(Some(err))
					case None =>
						callback(None)
						c.getList()
				}
			}
		)

		gui.showModalForm(form, process, processCancellable = true, Some(() => Unit))
	}

	private def createOrEdit(t: Option[TestData], c: AdminListController[TestData]): Unit = {
		val boxWidth = 20
		val formWidth = 60

		val descriptionBox: TextAreaTextBox = textarea(style := s"width:${boxWidth}em;height:5em;").render
		descriptionBox.setValue(t.map(_.description).getOrElse(""))
		val identifierBox: InputTextBox = makeTextInput(boxWidth, t.map(_.identifier).getOrElse(""))
		val displayNameBox: InputTextBox = makeTextInput(boxWidth, t.map(_.displayName).getOrElse(""))

		val form = makeForm(
			width = formWidth,
			title = t match {
				case Some(test) => s"Update ${test.displayName}"
				case None => "Create New Test"
			},
			description = "",
			elements = Array(
				("Name:", displayNameBox.text),
				("Identifier:", identifierBox.text),
				("Description:", descriptionBox.area)
			),
			useCancel = true,
			submitCaption = if (t.isDefined) "Update" else "Create"
		)

		def process(callback: (Option[String]) => Unit): Either[String,Form.Process] = {
			if (identifierBox.value().isEmpty || displayNameBox.value().isEmpty)
				Left("Name and Identifier are both required.")
			else {
				val data = TestUpdateData(identifier = identifierBox.value(), displayName = displayNameBox.value(), description = descriptionBox.value())
				Right(
					t match {
						case Some(test) =>
							CCABServer.updateTest(test.id, data) {
								case Left(err) => callback(Some(err.message))
								case Right(res) => res.error match {
									case Some(err) => callback(Some(err))
									case None =>
										callback(None)
										c.getList()
								}
							}
						case None =>
							CCABServer.createTest(data) {
								case Left(err) => callback(Some(err.message))
								case Right(res) => res.error match {
									case Some(err) => callback(Some(err))
									case None =>
										callback(None)
										c.getList()
								}
							}
					}
				)
			}
		}

		gui.showModalForm(form, process, processCancellable = true, Some(() => Unit))
	}
}

object AdminTestListController {
	def apply(gui: Gui): AdminListController[TestData] = new AdminListController[TestData](new AdminTestListController(gui))
}